import { Article, Category, Author } from '@/types/article';
import { calculateReadTime } from '@/utils/articleUtils';

// Cache for articles to avoid repeated fetches
let articlesCache: Article[] | null = null;
let categoriesCache: Category[] | null = null;
let authorsCache: Author[] | null = null;

/**
 * Get all article files by dynamically importing them
 */
export const getArticles = async (params?: {
  page?: number;
  limit?: number;
  category?: string;
  tag?: string;
}): Promise<{ articles: Article[]; total: number }> => {
  try {
    // Use cache if available
    if (!articlesCache) {
      // Dynamically import all article JSON files
      const articleFiles = import.meta.glob('/src/content/articles/*.json', { 
        eager: true,
        as: 'raw'  // Import as raw text to avoid JSON parsing issues
      });
      
      // Convert to array of articles
      articlesCache = Object.entries(articleFiles).map(([path, content]) => {
        try {
          const article = JSON.parse(content as string) as Article;
          
          // Ensure read time is calculated if not already set
          if (!article.readTime) {
            article.readTime = calculateReadTime(article.content);
          }
          
          return article;
        } catch (error) {
          console.error(`Error parsing article file ${path}:`, error);
          return null;
        }
      }).filter((article): article is Article => article !== null);
      
      // Sort by publish date (newest first)
      articlesCache.sort((a, b) => 
        new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
      );
    }
    
    let filtered = [...articlesCache];
    
    // Apply category filter
    if (params?.category) {
      filtered = filtered.filter(article =>
        article.categories.some(cat => cat.slug === params.category)
      );
    }
    
    // Apply tag filter
    if (params?.tag) {
      filtered = filtered.filter(article =>
        article.tags.some(tag => {
          // Handle both string tags and Tag objects
          if (typeof tag === 'string') {
            return tag === params.tag;
          }
          return tag.slug === params.tag;
        })
      );
    }
    
    const total = filtered.length;
    
    // Apply pagination
    if (params?.page !== undefined && params?.limit !== undefined) {
      const start = (params.page - 1) * params.limit;
      filtered = filtered.slice(start, start + params.limit);
    }
    
    return { articles: filtered, total };
  } catch (error) {
    console.error('Error fetching articles:', error);
    return { articles: [], total: 0 };
  }
};

/**
 * Get a specific article by its slug
 */
export const getArticleBySlug = async (slug?: string): Promise<Article | null> => {
  if (!slug) return null;
  
  try {
    // Use cache if available or fetch articles
    if (!articlesCache) {
      const { articles } = await getArticles();
      articlesCache = articles;
    }
    
    const found = articlesCache.find(article => article.slug === slug);
    return found || null;
  } catch (error) {
    console.error('Error fetching article by slug:', error);
    return null;
  }
};

/**
 * Get articles filtered by category
 */
export const getArticlesByCategory = async (categorySlug: string): Promise<Article[]> => {
  const { articles } = await getArticles({ category: categorySlug });
  return articles;
};

/**
 * Get all categories from all articles
 */
export const getAllCategories = async (): Promise<Category[]> => {
  try {
    if (!categoriesCache) {
      // Get all articles if not already cached
      if (!articlesCache) {
        const { articles } = await getArticles();
        articlesCache = articles;
      }
      
      // Extract unique categories based on id
      const categoriesMap = new Map<string, Category>();
      articlesCache.forEach(article => {
        article.categories.forEach(category => {
          if (!categoriesMap.has(category.id)) {
            categoriesMap.set(category.id, category);
          }
        });
      });
      
      categoriesCache = Array.from(categoriesMap.values());
    }
    
    return categoriesCache;
  } catch (error) {
    console.error('Error fetching all categories:', error);
    return [];
  }
};

/**
 * Get a specific category by its slug
 */
export const getCategoryBySlug = async (slug: string): Promise<Category | undefined> => {
  try {
    const categories = await getAllCategories();
    return categories.find(c => c.slug === slug);
  } catch (error) {
    console.error('Error fetching category by slug:', error);
    return undefined;
  }
};

/**
 * Get all authors from all articles
 */
export const getAllAuthors = async (): Promise<Author[]> => {
  try {
    if (!authorsCache) {
      // Get all articles if not already cached
      if (!articlesCache) {
        const { articles } = await getArticles();
        articlesCache = articles;
      }
      
      // Extract unique authors based on id
      const authorsMap = new Map<string, Author>();
      articlesCache.forEach(article => {
        if (!authorsMap.has(article.author.id)) {
          authorsMap.set(article.author.id, article.author);
        }
      });
      
      authorsCache = Array.from(authorsMap.values());
    }
    
    return authorsCache;
  } catch (error) {
    console.error('Error fetching all authors:', error);
    return [];
  }
};

/**
 * Get a specific author by their ID
 */
export const getAuthorById = async (id: string): Promise<Author | undefined> => {
  try {
    const authors = await getAllAuthors();
    return authors.find(a => a.id === id);
  } catch (error) {
    console.error('Error fetching author by id:', error);
    return undefined;
  }
}; 